'use client';

import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { type ComponentPropsWithoutRef, type KeyboardEvent, useCallback, useMemo, useRef, useSyncExternalStore } from 'react';

import { BirdLogo } from '@/components/branding/BirdLogo';
import { Stars } from '@/components/landing/Stars';
import { ActionRow } from '@/components/ui/ActionRow';
import { OtpCodeInput } from '@/components/ui/OtpCodeInput';
import { Typography } from '@/components/ui/Typography';
import { AppRoute } from '@/constants/routes';
import { getAuthLoginMethodStorageKey, getStoredAuthLoginMethod } from '@/features/auth/loginExperience';
import { getAuthSurfaceViewModel } from '@/features/auth/loginSurface';
import { getNextTabIndex } from '@/features/ui/tabsNavigation';
import { useDashboardCompactViewport } from '@/hooks/dashboard/useDashboardCompactViewport';
import { useI18n } from '@/hooks/i18n/useI18n';
import type { LoginRuntimeStatus } from '@/interfaces/LoginRuntimeStatus';
import type { SelectOption } from '@/interfaces/Select';

import { sanitizeNextPath } from '../routeAccess';
import { useLoginOtpFlow } from '../useLoginOtpFlow';
import styles from './LoginPageClient.module.scss';

const AUTH_LOGIN_METHOD_STORAGE_EVENT = 'chattenger:auth-login-method-change';
const getAuthLoginMethodSnapshot = () => {
    if (typeof window === 'undefined') {
        return 'otp';
    }

    return getStoredAuthLoginMethod(window.localStorage.getItem(getAuthLoginMethodStorageKey()));
};

const subscribeAuthLoginMethod = (onStoreChange: () => void) => {
    if (typeof window === 'undefined') {
        return () => undefined;
    }

    const storageKey = getAuthLoginMethodStorageKey();
    const handleMethodChange = (event: Event) => {
        if (event instanceof StorageEvent && event.key && event.key !== storageKey) {
            return;
        }

        onStoreChange();
    };

    window.addEventListener('storage', handleMethodChange);
    window.addEventListener(AUTH_LOGIN_METHOD_STORAGE_EVENT, handleMethodChange);

    return () => {
        window.removeEventListener('storage', handleMethodChange);
        window.removeEventListener(AUTH_LOGIN_METHOD_STORAGE_EVENT, handleMethodChange);
    };
};

type LoginPageClientProps = {
    nextPath: string;
    runtimeStatus?: LoginRuntimeStatus;
};

type LightweightLoginButtonProps = Omit<ComponentPropsWithoutRef<'button'>, 'children'> & {
    children: ComponentPropsWithoutRef<'button'>['children'];
    size?: 'compact' | 'default';
    variant?: 'ghost' | 'primary' | 'secondary' | 'tertiary';
};

const LightweightLoginButton = ({
    children,
    className,
    size = 'default',
    type = 'button',
    variant = 'primary',
    ...props
}: LightweightLoginButtonProps) => (
    <button
        className={clsx(
            styles.authButton,
            variant === 'primary' && styles.authButtonPrimary,
            variant === 'secondary' && styles.authButtonSecondary,
            (variant === 'ghost' || variant === 'tertiary') && styles.authButtonGhost,
            size === 'compact' && styles.authButtonCompact,
            className
        )}
        type={type}
        {...props}
    >
        {children}
    </button>
);

type LightweightLoginSelectProps = Omit<ComponentPropsWithoutRef<'select'>, 'children' | 'className' | 'size'> & {
    className?: string;
    'data-testid'?: string;
    label: string;
    options: SelectOption[];
};

const LightweightLoginSelect = ({ className, 'data-testid': dataTestId, id, label, options, ...props }: LightweightLoginSelectProps) => {
    const labelId = `${id}-label`;

    return (
        <div className={clsx(styles.authField, styles.authNativeField, className)} data-testid={dataTestId}>
            <label className={styles.authNativeLabel} data-testid={dataTestId ? `${dataTestId}-label` : undefined} htmlFor={id} id={labelId}>
                {label}
            </label>
            <span className={styles.authNativeSelectWrap}>
                <select
                    aria-labelledby={labelId}
                    className={clsx(styles.authNativeControl, styles.authNativeSelect)}
                    data-testid={dataTestId ? `${dataTestId}-control` : undefined}
                    id={id}
                    {...props}
                >
                    {options.map(({ label: optionLabel, value }) => (
                        <option key={value} value={value}>
                            {optionLabel}
                        </option>
                    ))}
                </select>
            </span>
        </div>
    );
};

type LightweightLoginInputProps = Omit<ComponentPropsWithoutRef<'input'>, 'className' | 'size'> & {
    className?: string;
    'data-testid'?: string;
    errorText?: string;
    hint?: string;
    inputClassName?: string;
    inputTestId?: string;
    label: string;
};

const LightweightLoginInput = ({
    className,
    'data-testid': dataTestId,
    errorText,
    hint,
    id,
    inputClassName,
    inputTestId,
    label,
    type = 'text',
    ...props
}: LightweightLoginInputProps) => {
    const message = errorText || hint;
    const messageId = message && id ? `${id}-message` : undefined;

    return (
        <div className={clsx(styles.authField, styles.authNativeField, className)} data-testid={dataTestId}>
            <label className={styles.authNativeLabel} data-testid={dataTestId ? `${dataTestId}-label` : undefined} htmlFor={id}>
                {label}
            </label>
            <span className={styles.authNativeControlWrap} data-testid={dataTestId ? `${dataTestId}-control` : undefined}>
                <input
                    aria-describedby={messageId}
                    aria-invalid={errorText ? true : undefined}
                    className={clsx(styles.authNativeControl, inputClassName)}
                    data-testid={inputTestId ?? (dataTestId ? `${dataTestId}-input` : undefined)}
                    id={id}
                    type={type}
                    {...props}
                />
            </span>
            {message && (
                <p
                    className={clsx(styles.authNativeMessage, errorText && styles.authNativeError)}
                    data-testid={dataTestId ? `${dataTestId}-message` : undefined}
                    id={messageId}
                    role={errorText ? 'alert' : undefined}
                >
                    {message}
                </p>
            )}
        </div>
    );
};

const WebQrLoginPanel = dynamic(() => import('@/features/auth/WebQrLoginPanel').then((module) => module.WebQrLoginPanel), {
    ssr: false,
});
const LoginAdvancedAuthContent = dynamic(
    () => import('../LoginAdvancedAuthContent/LoginAdvancedAuthContent').then((module) => module.LoginAdvancedAuthContent),
    {
        ssr: false,
    }
);

export const LoginPageClient = ({ nextPath, runtimeStatus }: LoginPageClientProps) => {
    const router = useRouter();
    const { dictionary, locale } = useI18n();
    const isCompactViewport = useDashboardCompactViewport();
    const method = useSyncExternalStore(subscribeAuthLoginMethod, getAuthLoginMethodSnapshot, () => 'otp');
    const methodTabsRef = useRef<Array<HTMLButtonElement | null>>([]);
    const targetPath = useMemo(() => sanitizeNextPath(nextPath) ?? AppRoute.Chats, [nextPath]);
    const showOtpFlow = method === 'otp';
    const isBackendUnavailable = runtimeStatus?.state === 'backend_unavailable';
    const loginOtpFlow = useLoginOtpFlow({
        dictionary: dictionary.auth,
        locale,
        router,
        targetPath,
    });
    const {
        canRequestNextOtpStep,
        canRequestOtp,
        canVerifyOtp,
        clearAuthError,
        countryOptionId,
        countryOptions,
        errorText,
        handleChangePhone,
        handleCountryOptionChange,
        handleOtpCodeComplete,
        handleOtpCodeChange,
        handlePhoneChange,
        handleRequestNextOtpStepClick,
        handleOpenChangedNumberRecovery,
        handleStartRecoveryClick,
        isChangedNumberRecoveryOpen,
        isSubmitting,
        nextOtpActionLabel,
        onRequestOtp,
        onVerifyOtp,
        otpCode,
        otpCodeLength,
        otpCooldownState,
        otpVariantHint,
        pendingApproval,
        phone,
        submitRequestOtp,
        step,
    } = loginOtpFlow;
    const isPendingDeviceApprovalStep = step === 'pendingApproval' && Boolean(pendingApproval);
    const shouldUseOtpContent = showOtpFlow || isPendingDeviceApprovalStep;
    const shouldShowAuthMethodToggle = !isPendingDeviceApprovalStep;
    const surfaceViewModel = useMemo(() => {
        if (isPendingDeviceApprovalStep) {
            return {
                cardTitle: dictionary.auth.pendingDeviceApprovalTitle,
                cardDescription: '',
            };
        }

        if (showOtpFlow && step === 'trustedBootstrapEmail') {
            return {
                cardTitle: dictionary.auth.trustedDeviceBootstrapEmailTitle,
                cardDescription: dictionary.auth.trustedDeviceBootstrapEmailDescription,
            };
        }

        if (showOtpFlow && step === 'recoveryOtp') {
            return {
                cardTitle: dictionary.auth.accountRecoveryTitle,
                cardDescription: dictionary.auth.accountRecoveryDescription,
            };
        }

        if (showOtpFlow && step === 'recoveryCodes') {
            return {
                cardTitle: dictionary.auth.accountRecoveryCodesTitle,
                cardDescription: dictionary.auth.recoveryCodesDescription,
            };
        }

        return getAuthSurfaceViewModel({
            auth: dictionary.auth,
        });
    }, [dictionary.auth, isPendingDeviceApprovalStep, showOtpFlow, step]);
    const persistMethod = useCallback((nextMethod: 'otp' | 'qr') => {
        if (typeof window === 'undefined') {
            return;
        }

        window.localStorage.setItem(getAuthLoginMethodStorageKey(), nextMethod);
        window.dispatchEvent(new Event(AUTH_LOGIN_METHOD_STORAGE_EVENT));
    }, []);

    const handleSelectOtpMethod = useCallback(() => {
        persistMethod('otp');
        clearAuthError();
    }, [clearAuthError, persistMethod]);

    const handleSelectQrMethod = useCallback(() => {
        persistMethod('qr');
        clearAuthError();
    }, [clearAuthError, persistMethod]);

    const handleMethodKeyDown = useCallback(
        (event: KeyboardEvent<HTMLButtonElement>) => {
            const currentIndex = Number(event.currentTarget.dataset.index ?? '-1');
            const nextIndex = getNextTabIndex({
                currentIndex,
                key: event.key,
                total: 2,
            });

            if (nextIndex === null || nextIndex === currentIndex) {
                return;
            }

            event.preventDefault();
            const nextMethod = nextIndex === 0 ? 'otp' : 'qr';
            persistMethod(nextMethod);
            methodTabsRef.current[nextIndex]?.focus();
        },
        [persistMethod]
    );

    const handlePhoneInputKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing || isSubmitting || !canRequestOtp) {
                return;
            }

            event.preventDefault();
            void submitRequestOtp();
        },
        [canRequestOtp, isSubmitting, submitRequestOtp]
    );

    const authContent = shouldUseOtpContent ? (
        step === 'phone' ? (
            <form className={styles.authForm} onSubmit={onRequestOtp} data-testid="features-auth-login-page-client-auth-form">
                <LightweightLoginSelect
                    id="country"
                    label={dictionary.auth.countryLabel}
                    options={countryOptions}
                    value={countryOptionId}
                    onChange={handleCountryOptionChange}
                    data-testid="features-auth-login-page-client-country-field"
                />
                <LightweightLoginInput
                    id="phone"
                    autoFocus
                    type={isCompactViewport ? 'text' : undefined}
                    inputTestId="features-auth-login-page-client-phone-input"
                    enterKeyHint="next"
                    inputMode={isCompactViewport ? 'numeric' : 'tel'}
                    maxLength={24}
                    onKeyDown={handlePhoneInputKeyDown}
                    autoComplete="tel"
                    label={dictionary.auth.phoneLabel}
                    placeholder={dictionary.auth.phonePlaceholder}
                    value={phone}
                    onChange={handlePhoneChange}
                    errorText={errorText || undefined}
                    data-testid="features-auth-login-page-client-phone-field"
                />

                <LightweightLoginButton
                    className={styles.authPrimaryButton}
                    type="submit"
                    disabled={isSubmitting || !canRequestOtp}
                    data-testid="features-auth-login-page-client-request-otp-button"
                >
                    {isSubmitting ? dictionary.auth.requesting : dictionary.auth.continue}
                </LightweightLoginButton>
                <LightweightLoginButton
                    className={styles.authGhostButton}
                    onClick={handleStartRecoveryClick}
                    type="button"
                    variant="ghost"
                    disabled={isSubmitting || !canRequestOtp}
                    data-testid="features-auth-login-page-client-start-recovery-button"
                >
                    {dictionary.auth.cantLogin}
                </LightweightLoginButton>
                <LightweightLoginButton
                    className={styles.authGhostButton}
                    onClick={handleOpenChangedNumberRecovery}
                    type="button"
                    variant="ghost"
                    disabled={isSubmitting}
                    data-testid="features-auth-login-page-client-changed-number-recovery-button"
                >
                    {dictionary.auth.changedNumberRecovery}
                </LightweightLoginButton>
                {isChangedNumberRecoveryOpen && (
                    <LoginAdvancedAuthContent
                        dictionary={dictionary}
                        flow={loginOtpFlow}
                        isCompactViewport={isCompactViewport}
                        mode="changedNumberRecovery"
                    />
                )}
            </form>
        ) : step === 'otp' ? (
            <form className={styles.authForm} onSubmit={onVerifyOtp} data-testid="features-auth-login-page-client-auth-form-2">
                <OtpCodeInput
                    id="otp"
                    autoFocus
                    errorText={errorText || undefined}
                    hint={otpVariantHint}
                    label={dictionary.auth.otpLabel}
                    value={otpCode}
                    onChange={handleOtpCodeChange}
                    onComplete={handleOtpCodeComplete}
                    dataTestId="features-auth-login-page-client-otp-field"
                    digitDataTestIdPrefix="features-auth-login-page-client-otp-digit"
                    length={otpCodeLength}
                />

                <ActionRow className={styles.authActionRow} data-testid="features-auth-login-page-client-landing-actions">
                    <LightweightLoginButton
                        className={styles.authPrimaryButton}
                        type="submit"
                        disabled={isSubmitting || !canVerifyOtp}
                        data-testid="features-auth-login-page-client-verify-otp-button"
                    >
                        {isSubmitting ? dictionary.auth.verifying : dictionary.auth.verify}
                    </LightweightLoginButton>
                    <LightweightLoginButton
                        className={styles.authGhostButton}
                        onClick={handleChangePhone}
                        type="button"
                        variant="ghost"
                        data-testid="features-auth-login-page-client-change-phone-button"
                    >
                        {dictionary.auth.changePhone}
                    </LightweightLoginButton>
                </ActionRow>

                {canRequestNextOtpStep && nextOtpActionLabel && (
                    <LightweightLoginButton
                        className={clsx(styles.authGhostButton, styles.authFallbackButton)}
                        disabled={isSubmitting || !otpCooldownState.canContinue}
                        onClick={handleRequestNextOtpStepClick}
                        type="button"
                        variant="ghost"
                        data-testid="features-auth-login-page-client-request-next-otp-step-button"
                    >
                        {nextOtpActionLabel}
                    </LightweightLoginButton>
                )}
            </form>
        ) : step === 'trustedBootstrapEmail' ? (
            <LoginAdvancedAuthContent
                dictionary={dictionary}
                flow={loginOtpFlow}
                isCompactViewport={isCompactViewport}
                mode="trustedBootstrapEmail"
            />
        ) : step === 'recoveryOtp' ? (
            <LoginAdvancedAuthContent dictionary={dictionary} flow={loginOtpFlow} isCompactViewport={isCompactViewport} mode="recoveryOtp" />
        ) : step === 'recoveryCodes' ? (
            <LoginAdvancedAuthContent dictionary={dictionary} flow={loginOtpFlow} isCompactViewport={isCompactViewport} mode="recoveryCodes" />
        ) : pendingApproval ? (
            <LoginAdvancedAuthContent dictionary={dictionary} flow={loginOtpFlow} isCompactViewport={isCompactViewport} mode="pendingApproval" />
        ) : null
    ) : (
        <WebQrLoginPanel targetPath={targetPath} />
    );
    const runtimeStatusAlert = isBackendUnavailable && (
        <section
            aria-describedby="login-runtime-alert-copy"
            aria-labelledby="login-runtime-alert-title"
            className={styles.runtimeAlert}
            role="alert"
            data-testid="features-auth-login-page-client-runtime-alert"
        >
            <Typography
                as="p"
                className={styles.runtimeAlertTitle}
                data-testid="features-auth-login-page-client-runtime-alert-title"
                id="login-runtime-alert-title"
                size="body"
                weight="semibold"
            >
                {dictionary.auth.runtimeUnavailableTitle}
            </Typography>
            <Typography
                as="p"
                className={styles.runtimeAlertCopy}
                data-testid="features-auth-login-page-client-runtime-alert-copy"
                id="login-runtime-alert-copy"
                size="caption"
                tone="muted"
            >
                {dictionary.auth.runtimeUnavailableDescription}
            </Typography>
        </section>
    );

    return (
        <main className={clsx(styles.authPageShell, styles['authPageShell--login'])} data-testid="features-auth-login-page-client-auth-page-shell">
            <Stars className={styles.authStars} dataTestId="features-auth-login-page-client-stars" />
            <section
                className={styles.authExperience}
                aria-label="Chattenger login experience"
                data-testid="features-auth-login-page-client-auth-experience"
            >
                <header className={styles.authHero} data-testid="features-auth-login-page-client-auth-hero">
                    <div className={styles.authHeroBrandColumn} data-testid="features-auth-login-page-client-auth-hero-brand-column">
                        <Typography
                            as="h1"
                            size="title-lg"
                            weight="bold"
                            className={clsx(styles.authBrandLockup, styles['authBrandLockup--hero'])}
                            data-testid="features-auth-login-page-client-auth-brand-lockup"
                        >
                            <BirdLogo alt="" className={styles.authBrandLogo} dataTestId="features-auth-login-page-client-auth-brand-logo" />
                            <span className={styles.authBrandWordmark} data-testid="features-auth-login-page-client-auth-brand-wordmark">
                                {dictionary.auth.title}
                            </span>
                        </Typography>
                    </div>
                </header>

                <div className={styles.authExperienceStack} data-testid="features-auth-login-page-client-auth-experience-stack">
                    <section className={clsx(styles.authCard, styles['authCard--login'])} data-testid="features-auth-login-page-client-auth-card">
                        <div className={styles.authCardSurface} data-testid="features-auth-login-page-client-auth-card-surface">
                            <div className={styles.authCardHeader} data-testid="features-auth-login-page-client-auth-card-header">
                                <Typography
                                    as="h2"
                                    className={styles.authCardTitle}
                                    data-testid="features-auth-login-page-client-auth-card-title"
                                    size="title-md"
                                    weight="semibold"
                                >
                                    {surfaceViewModel.cardTitle}
                                </Typography>
                            </div>

                            {runtimeStatusAlert}

                            {shouldShowAuthMethodToggle && (
                                <div
                                    className={styles.authMethodToggle}
                                    data-active={showOtpFlow ? 'otp' : 'qr'}
                                    role="tablist"
                                    aria-label={dictionary.auth.methodLabel}
                                    data-testid="features-auth-login-page-client-auth-method-toggle"
                                >
                                    <span
                                        aria-hidden="true"
                                        className={styles.authMethodIndicator}
                                        data-testid="features-auth-login-page-client-auth-method-indicator"
                                    />
                                    <button
                                        ref={(node) => {
                                            methodTabsRef.current[0] = node;
                                        }}
                                        aria-controls="auth-method-panel-otp"
                                        aria-selected={showOtpFlow}
                                        className={clsx(styles.authMethodButton, showOtpFlow && styles.authMethodButtonActive)}
                                        data-index={0}
                                        id="auth-method-tab-otp"
                                        onKeyDown={handleMethodKeyDown}
                                        onClick={handleSelectOtpMethod}
                                        role="tab"
                                        tabIndex={showOtpFlow ? 0 : -1}
                                        type="button"
                                        data-testid="features-auth-login-page-client-auth-method-tab-otp"
                                    >
                                        {dictionary.auth.methodOtp}
                                    </button>
                                    <button
                                        ref={(node) => {
                                            methodTabsRef.current[1] = node;
                                        }}
                                        aria-controls="auth-method-panel-qr"
                                        aria-selected={!showOtpFlow}
                                        className={clsx(styles.authMethodButton, !showOtpFlow && styles.authMethodButtonActive)}
                                        data-index={1}
                                        id="auth-method-tab-qr"
                                        onKeyDown={handleMethodKeyDown}
                                        onClick={handleSelectQrMethod}
                                        role="tab"
                                        tabIndex={!showOtpFlow ? 0 : -1}
                                        type="button"
                                        data-testid="features-auth-login-page-client-auth-method-tab-qr"
                                    >
                                        {dictionary.auth.methodQr}
                                    </button>
                                </div>
                            )}
                            <div
                                aria-labelledby={
                                    shouldShowAuthMethodToggle ? (showOtpFlow ? 'auth-method-tab-otp' : 'auth-method-tab-qr') : undefined
                                }
                                id={shouldShowAuthMethodToggle ? (showOtpFlow ? 'auth-method-panel-otp' : 'auth-method-panel-qr') : undefined}
                                role={shouldShowAuthMethodToggle ? 'tabpanel' : undefined}
                                data-testid="features-auth-login-page-client-tabpanel"
                            >
                                {authContent}
                            </div>
                        </div>
                    </section>
                </div>
            </section>
        </main>
    );
};
